// redux/slices/specialitiesSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { resetStore } from "./resetStore";
import { getServerUrl, getAuthHeaders } from "../../Utils/serverSelector";

/* ===================== HELPERS ===================== */

// ожидаем элементы: { profession, professionHe, professionEn?, category? ... }
const normalize = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.specialities)) return data.specialities;
  return [];
};

export const pickSpecByLang = (spec = {}, lang = "ru") => {
  const L = (lang || "ru").toLowerCase();
  const p = spec.profession?.trim?.() || "";
  const ph = spec.professionHe?.trim?.() || "";
  const pe = spec.professionEn?.trim?.() || "";
  if (L === "he") return ph || p || pe || "";
  if (L === "en") return pe || p || ph || "";
  return p || pe || ph || "";
};

// общие конфиги для приватных запросов
const authJsonConfig = async () => {
  const headers = await getAuthHeaders();
  return {
    headers,
    withCredentials: true,
    validateStatus: () => true,
  };
};

const authJsonConfigWithJson = async () => {
  const base = await authJsonConfig();
  return {
    ...base,
    headers: {
      ...base.headers,
      "Content-Type": "application/json",
    },
  };
};

/* ===================== THUNKS ===================== */

// Старый thunk (совместимость): кладёт в "текущие" specialities/selectedSpecialities
export const fetchSpecialities = createAsyncThunk(
  "specialities/fetchSpecialities",
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");
      const cfg = await authJsonConfig();

      const res = await axios.get(
        `${getServerUrl()}/specialities/${uid}`,
        cfg
      );

      if (res.status === 404) {
        return { uid: String(uid), items: [] };
      }
      if (res.status !== 200) {
        throw new Error(
          res.data?.message ||
          res.data?.error ||
          `Failed to fetch specialities (HTTP ${res.status})`
        );
      }

      const items = normalize(res.data);
      return { uid: String(uid), items };
    } catch (e) {
      return rejectWithValue(
        e?.response?.data ||
        e?.message ||
        "fetchSpecialities failed"
      );
    }
  }
);

// Обновление/сохранение
export const updateSpecialities = createAsyncThunk(
  "specialities/updateSpecialities",
  async ({ uid, specialities }, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");
      const cfg = await authJsonConfigWithJson();

      const res = await axios.post(
        `${getServerUrl()}/specialities/${uid}`,
        { specialities },
        cfg
      );

      if (res.status !== 200 && res.status !== 201) {
        throw new Error(
          res.data?.message ||
          res.data?.error ||
          `Failed to update specialities (HTTP ${res.status})`
        );
      }

      return res.data;
    } catch (e) {
      return rejectWithValue(
        e?.response?.data ||
        e?.message ||
        "updateSpecialities failed"
      );
    }
  }
);

export const saveSpecialities = createAsyncThunk(
  "specialities/saveSpecialities",
  async ({ uid, specialities }, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");
      const cfg = await authJsonConfigWithJson();

      const res = await axios.post(
        `${getServerUrl()}/specialities/${uid}`,
        { specialities },
        cfg
      );

      if (res.status !== 200 && res.status !== 201) {
        throw new Error(
          res.data?.message ||
          res.data?.error ||
          `Failed to save specialities (HTTP ${res.status})`
        );
      }

      return res.data;
    } catch (e) {
      return rejectWithValue(
        e?.response?.data ||
        e?.message ||
        "saveSpecialities failed"
      );
    }
  }
);

// НОВОЕ: фетч в КЭШ по uid (не трогаем "текущие")
export const fetchSpecialitiesToCache = createAsyncThunk(
  "specialities/fetchSpecialitiesToCache",
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");
      const cfg = await authJsonConfig();

      const res = await axios.get(
        `${getServerUrl()}/specialities/${uid}`,
        cfg
      );

      if (res.status === 404) {
        return { uid: String(uid), items: [] };
      }
      if (res.status !== 200) {
        throw new Error(
          res.data?.message ||
          res.data?.error ||
          `Failed to fetch specialities to cache (HTTP ${res.status})`
        );
      }

      const items = normalize(res.data);
      return { uid: String(uid), items };
    } catch (e) {
      return rejectWithValue(
        e?.response?.data ||
        e?.message ||
        "fetchSpecialitiesToCache failed"
      );
    }
  }
);

// НОВОЕ: батч — загрузить специализации сразу для набора uid
export const fetchProfessionsForUids = createAsyncThunk(
  "specialities/fetchProfessionsForUids",
  async (uids = [], { dispatch, rejectWithValue }) => {
    try {
      const uniq = Array.from(
        new Set((uids || []).map((u) => String(u)).filter(Boolean))
      );

      const results = await Promise.allSettled(
        uniq.map((u) => dispatch(fetchSpecialitiesToCache(u)).unwrap())
      );

      return { requested: uniq, results };
    } catch (e) {
      return rejectWithValue(
        e?.message || "fetchProfessionsForUids failed"
      );
    }
  }
);

// === ПУБЛИЧНО: получить специализации мастера по uid (без headers) ===
export const fetchPublicSpecialities = createAsyncThunk(
  "specialities/fetchPublicSpecialities",
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");
      const base = getServerUrl().replace(/\/$/, "");

      const res = await axios.get(
        `${base}/public/${uid}/specialities/`,
        { validateStatus: () => true }
      );

      if (res.status === 404) {
        return { uid: String(uid), items: [] };
      }
      if (res.status !== 200) {
        throw new Error(
          res.data?.message ||
          res.data?.error ||
          `Failed to fetch public specialities (HTTP ${res.status})`
        );
      }

      const items = normalize(res.data);
      return { uid: String(uid), items };
    } catch (e) {
      return rejectWithValue(
        e?.response?.data ||
        e?.message ||
        "fetchPublicSpecialities failed"
      );
    }
  }
);

// === ПУБЛИЧНО: батч для множества uid (кладём только в byUid; «текущие» не трогаем напрямую) ===
export const fetchPublicProfessionsForUids = createAsyncThunk(
  "specialities/fetchPublicProfessionsForUids",
  async (uids = [], { dispatch, rejectWithValue }) => {
    try {
      const uniq = Array.from(
        new Set((uids || []).map((u) => String(u)).filter(Boolean))
      );

      const results = await Promise.allSettled(
        uniq.map((u) => dispatch(fetchPublicSpecialities(u)).unwrap())
      );

      return { requested: uniq, results };
    } catch (e) {
      return rejectWithValue(
        e?.message || "fetchPublicProfessionsForUids failed"
      );
    }
  }
);

/* ===================== STATE ===================== */

const initialState = {
  // «текущие» (совместимость со старым кодом экранов редактирования мастера)
  specialities: [],
  selectedSpecialities: [],

  // статусы/ошибка «текущих»
  status: "idle",
  error: null,

  // НОВОЕ: кэш по uid
  byUid: {},        // { [uid]: Speciality[] }
  statusByUid: {},  // { [uid]: 'idle' | 'loading' | 'succeeded' | 'failed' }
  errorByUid: {},   // { [uid]: string | null }

  // общий батч-статус
  batchStatus: "idle",
  batchError: null,
};

/* ===================== SLICE ===================== */

const specialitiesSlice = createSlice({
  name: "specialities",
  initialState,
  reducers: {
    toggleSpeciality: (state, action) => {
      const index = state.specialities.findIndex((s) => s.id === action.payload);
      if (index !== -1) {
        state.specialities[index].isChecked = !state.specialities[index].isChecked;
        if (state.specialities[index].isChecked) {
          state.selectedSpecialities.push(state.specialities[index].profession);
        } else {
          state.selectedSpecialities = state.selectedSpecialities.filter(
            (s) => s !== state.specialities[index].profession
          );
        }
      }
    },
    addNewSpeciality: (state, action) => {
      state.specialities.push(action.payload);
      state.selectedSpecialities.push(action.payload.profession);
    },
    setSpecialities: (state, action) => {
      state.specialities = action.payload.map((item) => ({
        ...item,
        isChecked: state.selectedSpecialities.includes(item.profession),
      }));
    },
  },
  extraReducers: (builder) => {
    builder
      /* ---------- fetchSpecialities (старый) ---------- */
      .addCase(fetchSpecialities.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchSpecialities.fulfilled, (state, action) => {
        state.status = "succeeded";
        const { uid, items } = action.payload || { uid: "", items: [] };

        // 1) Текущие списки (как было раньше)
        state.specialities = items;
        state.selectedSpecialities = items.map((s) => s.profession);
        state.error = null;

        // 2) Параллельно кладём в кэш по uid
        if (uid) state.byUid[uid] = items;
        if (uid) state.statusByUid[uid] = "succeeded";
        if (uid) state.errorByUid[uid] = null;
      })
      .addCase(fetchSpecialities.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "fetchSpecialities failed";
      })

      /* ---------- update/save ---------- */
      .addCase(updateSpecialities.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(updateSpecialities.fulfilled, (state) => {
        state.status = "succeeded";
      })
      .addCase(updateSpecialities.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "updateSpecialities failed";
      })
      .addCase(saveSpecialities.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(saveSpecialities.fulfilled, (state) => {
        state.status = "succeeded";
      })
      .addCase(saveSpecialities.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "saveSpecialities failed";
      })

      /* ---------- НОВОЕ: fetchSpecialitiesToCache ---------- */
      .addCase(fetchSpecialitiesToCache.pending, (state, action) => {
        const uid = String(action.meta.arg || "");
        if (uid) {
          state.statusByUid[uid] = "loading";
          state.errorByUid[uid] = null;
        }
      })
      .addCase(fetchSpecialitiesToCache.fulfilled, (state, action) => {
        const { uid, items } = action.payload || { uid: "", items: [] };
        if (uid) {
          state.byUid[uid] = items;
          state.statusByUid[uid] = "succeeded";
          state.errorByUid[uid] = null;
        }
      })
      .addCase(fetchSpecialitiesToCache.rejected, (state, action) => {
        const uid = String(action.meta?.arg || "");
        if (uid) {
          state.statusByUid[uid] = "failed";
          state.errorByUid[uid] =
            action.payload ||
            action.error?.message ||
            "fetchSpecialitiesToCache failed";
        }
      })

      /* ---------- НОВОЕ: fetchProfessionsForUids (batch) ---------- */
      .addCase(fetchProfessionsForUids.pending, (state) => {
        state.batchStatus = "loading";
        state.batchError = null;
      })
      .addCase(fetchProfessionsForUids.fulfilled, (state) => {
        state.batchStatus = "succeeded";
      })
      .addCase(fetchProfessionsForUids.rejected, (state, action) => {
        state.batchStatus = "failed";
        state.batchError =
          action.payload ||
          action.error?.message ||
          "fetchProfessionsForUids failed";
      })

      /* ---------- ПУБЛИЧНО: fetchPublicSpecialities ---------- */
      .addCase(fetchPublicSpecialities.pending, (state, action) => {
        state.status = "loading";
        state.error = null;
        const uid = String(action.meta.arg || "");
        if (uid) {
          state.statusByUid[uid] = "loading";
          state.errorByUid[uid] = null;
        }
      })
      .addCase(fetchPublicSpecialities.fulfilled, (state, action) => {
        state.status = "succeeded";
        const { uid, items } = action.payload || { uid: "", items: [] };

        // 1) Обновляем «текущие» (как и в приватном fetchSpecialities)
        state.specialities = items;
        state.selectedSpecialities = items.map((s) => s.profession);
        state.error = null;

        // 2) Параллельно кладём в кэш по uid
        if (uid) {
          state.byUid[uid] = items;
          state.statusByUid[uid] = "succeeded";
          state.errorByUid[uid] = null;
        }
      })
      .addCase(fetchPublicSpecialities.rejected, (state, action) => {
        state.status = "failed";
        const msg =
          action.payload ||
          action.error?.message ||
          "fetchPublicSpecialities failed";
        state.error = msg;
        const uid = String(action.meta?.arg || "");
        if (uid) {
          state.statusByUid[uid] = "failed";
          state.errorByUid[uid] = msg;
        }
      })

      /* ---------- ПУБЛИЧНО: fetchPublicProfessionsForUids ---------- */
      .addCase(fetchPublicProfessionsForUids.pending, (state) => {
        state.batchStatus = "loading";
        state.batchError = null;
      })
      .addCase(fetchPublicProfessionsForUids.fulfilled, (state) => {
        state.batchStatus = "succeeded";
      })
      .addCase(fetchPublicProfessionsForUids.rejected, (state, action) => {
        state.batchStatus = "failed";
        state.batchError =
          action.payload ||
          action.error?.message ||
          "fetchPublicProfessionsForUids failed";
      })

      /* ---------- reset ---------- */
      .addCase(resetStore, () => initialState);
  },
});

export const { toggleSpeciality, addNewSpeciality, setSpecialities } =
  specialitiesSlice.actions;
export default specialitiesSlice.reducer;

/* ===================== SELECTORS ===================== */

export const selectSpecialitiesByUid = (state, uid) =>
  state?.specialities?.byUid?.[String(uid)] || [];

export const selectSpecialitiesStatusByUid = (state, uid) =>
  state?.specialities?.statusByUid?.[String(uid)] || "idle";

export const selectSpecialitiesErrorByUid = (state, uid) =>
  state?.specialities?.errorByUid?.[String(uid)] || null;

export const selectSpecialitiesTextByUid =
  (uid, lang = "ru", limit) =>
  (state) => {
    const arr = state?.specialities?.byUid?.[String(uid)] || [];
    const list = arr
      .map((s) => pickSpecByLang(s, lang))
      .filter(Boolean);
    const sliced = typeof limit === "number" ? list.slice(0, limit) : list;
    return sliced.join(", ");
  };
